import { LinkIcon } from '@phosphor-icons/react';
import { Components } from 'react-markdown';
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownRehypeRawSanitizePlugins } from '@/lib/markdown-rehype-plugins';
import {
  CITATION_SUP_CLASSNAME,
  CITATION_SUP_CLASSNAME_LIGHT,
  CITATION_SUP_STYLE,
  CITATION_SUP_STYLE_LIGHT,
} from '@/shared/citationSupBadge';

export interface MarkdownComponentOptions {
  /** Body text inherits font-size from a parent (e.g. courtroom controls). */
  inheritFontSize?: boolean;
  /** Light page background (e.g. timeline PDF export); uses dark text instead of white. */
  lightBackground?: boolean;
}

export function createMarkdownComponents(options: MarkdownComponentOptions = {}): Components {
  const { inheritFontSize = false, lightBackground = false } = options;

  const bodyColor = lightBackground ? 'text-inherit' : 'text-white';
  const bodySize = inheritFontSize || lightBackground ? 'text-inherit' : 'text-base';
  const h1Size = inheritFontSize ? 'text-[1.33em]' : 'text-lg sm:text-2xl';
  const h2Size = inheritFontSize ? 'text-[1.2em]' : 'text-md sm:text-xl';
  const h3Size = inheritFontSize ? 'text-[1.1em]' : 'text-base sm:text-lg';
  const blockquoteSize = inheritFontSize || lightBackground ? 'text-inherit' : 'text-[16px]';
  const blockquoteParagraphSize = inheritFontSize || lightBackground ? 'text-inherit' : 'text-[18px]';

  const h1Color = lightBackground ? 'text-black' : 'text-white';
  const h2Color = lightBackground ? 'text-blue-700' : 'text-blue-400';
  const h3Color = lightBackground ? 'text-blue-600' : 'text-blue-300';
  const accentBorder = lightBackground ? 'border-blue-600' : 'border-blue-300';
  const linkColor = lightBackground
    ? 'text-blue-700 hover:text-blue-900'
    : 'text-blue-300 hover:text-blue-500';
  const tableBorder = lightBackground ? 'border-gray-300' : 'border-gray-700';
  const thColors = lightBackground
    ? 'border-gray-300 bg-gray-100 text-black'
    : 'border-gray-700 bg-gray-800';
  const trColors = lightBackground
    ? 'border-gray-300 even:bg-gray-50 odd:bg-white'
    : 'border-gray-700 even:bg-gray-600 odd:bg-gray-900';
  const tdBorder = lightBackground ? 'border-gray-300' : 'border-gray-700';

  return {
    h1: ({ children }) => (
      <h1 className={`w-full ${h1Color} font-semibold ${h1Size} border-b-[2px] border-solid ${accentBorder} m-0 mb-1 p-0 pb-1`}>
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className={`${h2Color} font-semibold ${h2Size} p-0 m-0 mb-1`}>
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className={`${h3Color} font-medium ${h3Size} p-0 m-0 mt-3 mb-1 first:mt-0`}>
        {children}
      </h3>
    ),
    ul: ({ children }) => (
      <ul className='list-disc list-outside p-0 m-0 ml-8'>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className='list-decimal list-outside p-0 m-0 ml-8'>{children}</ol>
    ),
    li: ({ children }) => (
      <li className={`${bodyColor} font-light ${bodySize} leading-snug m-0 pb-2`}>{children}</li>
    ),
    p: ({ children, ...props }) => {
      const isInBlockquote = props.className?.includes('blockquote');
      if (isInBlockquote) {
        return <p className={`${bodyColor} font-light ${blockquoteParagraphSize} leading-snug m-0 my-1`}>{children}</p>;
      }
      return <p className={`${bodyColor} font-light ${bodySize} leading-snug m-0 pb-2`}>{children}</p>;
    },
    a: ({ node, ...props }) => {
      const href = node?.properties?.href as string;
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`${bodySize} ${linkColor} no-underline inline-flex flex-row flex-wrap items-center gap-1 break-all`}
        >
          {props.children}
          <LinkIcon size={14} weight="light" className='mt-[-12px]' />
        </a>
      );
    },
    // Match answer-item citation chips (grey pill + 9px superscript).
    sup: ({ children }) => (
      <sup
        className={lightBackground ? CITATION_SUP_CLASSNAME_LIGHT : CITATION_SUP_CLASSNAME}
        style={lightBackground ? CITATION_SUP_STYLE_LIGHT : CITATION_SUP_STYLE}
      >
        {children}
      </sup>
    ),
    table: ({ children }) => (
      <div className="my-2 w-full min-w-0 max-w-full overflow-x-auto custom-scrollbar2">
        <table className={`w-max min-w-full table-auto border-collapse border ${tableBorder} ${bodySize}`}>{children}</table>
      </div>
    ),
    th: ({ children }) => (
      <th className={`min-w-32 max-w-[24rem] border border-solid p-2 text-left align-top font-semibold tracking-wider wrap-break-word ${thColors}`}>
        {children}
      </th>
    ),
    tr: ({ children }) => (
      <tr className={`border border-solid ${trColors}`}>
        {children}
      </tr>
    ),
    td: ({ children }) => (
      <td className={`min-w-32 max-w-[24rem] border border-solid p-2 align-top leading-relaxed wrap-break-word ${tdBorder}`}>{children}</td>
    ),
    blockquote: ({ children }) => (
      <blockquote className={`${bodyColor} font-light ${blockquoteSize} leading-snug m-0 my-1 py-1 pl-4 border-l-[3px] border-solid ${accentBorder} italic`}>
        {children}
      </blockquote>
    ),
    hr: () => <hr className={`${accentBorder} border-solid border-b-[2px] w-full my-2`} />,
  };
}

export const markdownComponents = createMarkdownComponents();
export const inheritFontSizeMarkdownComponents = createMarkdownComponents({ inheritFontSize: true });
export const exportMarkdownComponents = createMarkdownComponents({ lightBackground: true });

export function isTableSeparatorLine(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && /[-:]/.test(trimmed) && /^\|?[\s|:-]+\|?\s*$/.test(trimmed);
}

/** Lines that complete a table row split across lines (e.g. a lone closing `|`). */
export function isSplitTableRowContinuation(line: string): boolean {
  const trimmed = line.trim();
  if (trimmed === '|' || /^\|\s*$/.test(trimmed)) return true;
  // Trailing citation refs with closing pipe: "155 |", "156 157 |"
  if (/^[\d\s]+\|\s*$/.test(trimmed)) return true;
  return false;
}

/** Join table rows split across lines (e.g. trailing citation refs on the next line). */
export function repairBrokenTableRows(markdown: string): string {
  const lines = markdown.split('\n');
  const out: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    const trimmed = line.trim();

    if (isTableSeparatorLine(line)) {
      out.push(line);
      continue;
    }

    if (trimmed.startsWith('|')) {
      while (i + 1 < lines.length && isSplitTableRowContinuation(lines[i + 1].trim())) {
        const nextTrimmed = lines[i + 1].trim();
        if (nextTrimmed === '|' || /^\|\s*$/.test(nextTrimmed)) {
          if (line.trimEnd().endsWith('|')) {
            // Spurious lone pipe after an already-complete row; discard it.
            i++;
            break;
          }
          line = `${line.trimEnd()} |`;
        } else if (line.trimEnd().endsWith('|')) {
          // Citation refs after a premature row closer: "| a | b |" + "5 6 |"
          line = `${line.trimEnd().slice(0, -1).trimEnd()} ${nextTrimmed}`;
        } else {
          line = `${line.trimEnd()} ${nextTrimmed}`;
        }
        i++;
      }
      out.push(line);
      continue;
    }

    out.push(line);
  }

  return out.join('\n');
}

type MarkdownSegment = { content: string; isCode: boolean };

/** Split markdown into fenced code blocks and prose regions. */
export function splitByFencedCodeBlocks(markdown: string): MarkdownSegment[] {
  const lines = markdown.split('\n');
  const segments: MarkdownSegment[] = [];
  let currentLines: string[] = [];
  let inFence = false;

  const flush = () => {
    if (currentLines.length > 0) {
      segments.push({ content: currentLines.join('\n'), isCode: inFence });
      currentLines = [];
    }
  };

  for (const line of lines) {
    const isFenceDelimiter = /^(`{3,}|~{3,})/.test(line.trimStart());
    if (isFenceDelimiter) {
      if (inFence) {
        currentLines.push(line);
        flush();
        inFence = false;
      } else {
        flush();
        inFence = true;
        currentLines.push(line);
      }
      continue;
    }

    currentLines.push(line);
  }

  flush();
  return segments;
}

/** Maps color names (as the AI writes them in parentheses) to CSS color values. */
const COLOR_NAME_MAP: Record<string, string> = {
  red:    '#ef4444',
  green:  '#22c55e',
  blue:   '#3b82f6',
  yellow: '#eab308',
  orange: '#f97316',
  purple: '#a855f7',
  pink:   '#ec4899',
  cyan:   '#06b6d4',
  teal:   '#14b8a6',
  gray:   '#6b7280',
  grey:   '#6b7280',
  brown:  '#92400e',
  violet: '#7c3aed',
  indigo: '#4f46e5',
  gold:   '#d97706',
  maroon: '#991b1b',
  navy:   '#1e3a5f',
};

/**
 * Converts speaker-labeled transcript lines into colored HTML spans.
 *
 * The AI outputs lines like:
 *   Speaker 1 (Red): text…
 *   System (Blue): text…
 *
 * This detects the color name in parentheses (e.g. "(Red)"), maps it to a
 * CSS value, and wraps the whole line in `<span style="color:…">` so the
 * entire line renders in the speaker's color in every display view.
 * The stored description stays as plain text so colors carry over automatically
 * when an event is created from a chat answer; coloring is applied at render time.
 * Lines inside fenced code blocks are left untouched.
 */
export function colorizeSpeakerLines(markdown: string): string {
  if (!markdown) return markdown ?? '';
  let inCodeBlock = false;
  return markdown.split('\n').map((line) => {
    if (line.startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      return line;
    }
    if (inCodeBlock) return line;
    // Skip structural markdown lines — wrapping them in a <span> breaks block-level
    // parsing (table rows lose their leading `|`, list items lose their `-`/`*`/`+`
    // marker, headings and blockquotes are similarly mangled).
    const trimmedLine = line.trimStart();
    if (
      trimmedLine.startsWith('|') ||
      /^[-*+]\s/.test(trimmedLine) ||
      /^#{1,6}\s/.test(trimmedLine) ||
      trimmedLine.startsWith('>')
    ) {
      return line;
    }
    // Match "Speaker N (ColorName):" or "System (ColorName):" etc.
    const colorMatch = /\(([^)]+)\)\s*:/.exec(line);
    if (colorMatch) {
      const cssColor = COLOR_NAME_MAP[colorMatch[1].toLowerCase().trim()];
      if (cssColor) {
        // Remove the "(ColorName)" label — the line color already conveys speaker identity.
        const withoutColorWord = line.replace(/\s*\([^)]+\)(\s*:)/, '$1');
        const htmlContent = withoutColorWord.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
        return `<span style="color: ${cssColor}">${htmlContent}</span>`;
      }
    }
    return line;
  }).join('\n');
}

/** One citation marker. The number is group 1. */
const CITATION_SUP = /<sup>\s*(\d+)\s*<\/sup>/gi;

interface CitationSupSpan {
  number: string;
  start: number;
  end: number;
  tag: string;
}

/**
 * Collapse a run of citation markers to one marker per number, in numeric order.
 *
 * ``1 1``, ``1 2 1 2``, and ``1 2 3 1 2`` all become the sorted set of those
 * numbers. A run is markers separated only by whitespace, so the same numbers
 * in different sentences stay put.
 */
export function collapseAdjacentCitationSups(markdown: string): string {
  const spans = citationSupSpans(markdown);
  if (spans.length < 2) return markdown;

  const runs = citationSupRuns(markdown, spans);
  let result = markdown;
  for (let runIndex = runs.length - 1; runIndex >= 0; runIndex -= 1) {
    const run = runs[runIndex];
    if (run.length < 2) continue;
    const collapsed = sortedCitationSupRun(markdown, run);
    if (collapsed == null) continue;
    const start = run[0].start;
    const end = run[run.length - 1].end;
    result = result.slice(0, start) + collapsed + result.slice(end);
  }
  return result;
}

function citationSupSpans(markdown: string): CitationSupSpan[] {
  const spans: CitationSupSpan[] = [];
  for (const match of markdown.matchAll(CITATION_SUP)) {
    const tag = match[0];
    const start = match.index ?? 0;
    spans.push({ number: match[1], start, end: start + tag.length, tag });
  }
  return spans;
}

/** Group markers whose only separation is whitespace. */
function citationSupRuns(markdown: string, spans: CitationSupSpan[]): CitationSupSpan[][] {
  const runs: CitationSupSpan[][] = [];
  let current: CitationSupSpan[] = [];
  for (const span of spans) {
    const gap = current.length === 0 ? '' : markdown.slice(current[current.length - 1].end, span.start);
    if (current.length > 0 && !/^\s*$/.test(gap)) {
      runs.push(current);
      current = [];
    }
    current.push(span);
  }
  if (current.length > 0) runs.push(current);
  return runs;
}

/**
 * One marker per citation number, lowest first.
 *
 * Returns null when the run is already that set, so an ordered run is left
 * byte-for-byte alone. The separator between the first two markers is reused
 * for the rebuilt run.
 */
function sortedCitationSupRun(source: string, run: CitationSupSpan[]): string | null {
  const firstByNumber = new Map<number, CitationSupSpan>();
  for (const span of run) {
    const value = Number(span.number);
    if (!firstByNumber.has(value)) firstByNumber.set(value, span);
  }
  const kept = [...firstByNumber.entries()]
    .sort((left, right) => left[0] - right[0])
    .map((entry) => entry[1]);
  if (kept.length === run.length && kept.every((span, index) => span === run[index])) return null;
  const separator = source.slice(run[0].end, run[1].start);
  return kept.map((span) => span.tag).join(separator);
}

function normalizeProseSegment(markdown: string): string {
  const withParagraphBreaks = normalizeLegacyParagraphDelimiters(
    collapseAdjacentCitationSups(markdown),
  );
  const withLineBreaks = withParagraphBreaks.replace(/<br\s*\/?>/gi, '\n');
  const withColorizedSpeakers = colorizeSpeakerLines(withLineBreaks);
  return repairBrokenTableRows(withColorizedSpeakers);
}

const LIST_ITEM_LINE = /^[ \t]*(?:[-*+]|\d+[.)])\s/;
const BLANK_LINE = /^[ \t]*$/;
/** Openers that end the preceding list item, so the backward scan stops on them. */
const BLOCK_OPENER_LINE = /^[ \t]*(?:#{1,6}\s|>|\||`{3,}|~{3,})/;
/** Ceiling on the backward scan so a long unbroken paragraph cannot walk the whole answer. */
const MAX_LIST_ITEM_SCAN_LINES = 25;

/**
 * Whether the text ending at ``end`` is inside a list item.
 *
 * The delimiter rarely sits on the item's own line. Enriching an event puts
 * each citation on a line of its own, and the delimiter can open a line, which
 * leaves a continuation line — or nothing at all — between it and the item
 * text. Both are still the same item, so the scan walks back over continuation
 * lines. A blank line or a new block does end the item and stops the scan.
 */
function endsInsideListItem(whole: string, end: number): boolean {
  let lineEnd = end;

  for (let scanned = 0; scanned < MAX_LIST_ITEM_SCAN_LINES; scanned++) {
    const lineStart = whole.lastIndexOf('\n', lineEnd - 1) + 1;
    const line = whole.slice(lineStart, lineEnd);

    if (LIST_ITEM_LINE.test(line)) return true;
    // An empty first slice means the delimiter opens its own line, which is not
    // a blank line in the source; only an earlier one closes the item.
    if (BLANK_LINE.test(line) && scanned > 0) return false;
    if (BLOCK_OPENER_LINE.test(line)) return false;
    if (lineStart === 0) return false;

    lineEnd = lineStart - 1;
  }

  return false;
}

/**
 * Replace the assistant service's legacy answer-part delimiter with a Markdown
 * block break.
 *
 * The service joins independently generated answer parts with ``</p><p>\n``
 * even though the surrounding response is Markdown, not an HTML paragraph
 * tree. Left alone, `rehypeRaw` parses the unmatched tags into a pair of empty
 * paragraphs after every block, which show up as phantom vertical gaps.
 *
 * Between two list items the replacement is a single newline rather than a
 * blank line: a blank line makes CommonMark treat the list as loose and wrap
 * every item in a `<p>`, which would add that paragraph's spacing on top of
 * the spacing list items already carry. A delimiter that already opens its own
 * line contributes no newline of its own, since the break is there in the
 * source and emitting another would produce the blank line being avoided.
 */
export function normalizeLegacyParagraphDelimiters(markdown: string): string {
  return markdown.replace(
    /[ \t]*<\/p>[ \t]*<p>[ \t]*(?:\r?\n)?/gi,
    (match: string, offset: number, whole: string) => {
      const restStart = offset + match.length;
      const restEnd = whole.indexOf('\n', restStart);
      const followingLine = whole.slice(restStart, restEnd === -1 ? undefined : restEnd);
      const staysInsideOneList =
        endsInsideListItem(whole, offset) && LIST_ITEM_LINE.test(followingLine);
      const opensLine = offset === 0 || whole[offset - 1] === '\n';

      if (staysInsideOneList) return opensLine ? '' : '\n';
      return opensLine ? '\n' : '\n\n';
    },
  );
}

export function normalizePresentationMarkdown(markdown?: string): string {
  if (!markdown?.trim()) return '';

  const normalized = splitByFencedCodeBlocks(markdown)
    .map((segment) => (segment.isCode ? segment.content : normalizeProseSegment(segment.content)))
    .join('\n');

  return normalized.trimEnd();
}

interface PresentationMarkdownProps {
  children?: string;
  className?: string;
  /** When true, body text inherits font-size from a parent (e.g. courtroom controls). */
  inheritFontSize?: boolean;
  /** When true, use dark text for light backgrounds (e.g. timeline PDF export). */
  lightBackground?: boolean;
}

export function PresentationMarkdown({
  children = '',
  className,
  inheritFontSize = false,
  lightBackground = false,
}: PresentationMarkdownProps) {
  const normalized = useMemo(() => normalizePresentationMarkdown(children), [children]);
  const components = useMemo(
    () => createMarkdownComponents({ inheritFontSize, lightBackground }),
    [inheritFontSize, lightBackground],
  );

  return (
    <div className={className}>
      <ReactMarkdown
        components={components}
        rehypePlugins={markdownRehypeRawSanitizePlugins}
        remarkPlugins={[remarkGfm]}
      >
        {normalized}
      </ReactMarkdown>
    </div>
  );
}
